import React from 'react';
import styled from 'styled-components';
import shopSelectors from 'services/shop/selectors';
import { formatCityStateZip } from 'features/report/pages/ReportPage/utils';
import { formatCurrency } from 'utils/formatCurrency';
import { ICashDepositResponseData } from 'features/cashier/services/types/api';
import { CASH_DEPOSIT_CASH_TYPE, cashDepositConfigs } from './CashTypeItem';

type ICashDepositPrintPreviewProps = {
  data: ICashDepositResponseData;
};

const getCashTypeLabel = (cashType: string): string => {
  const config = cashDepositConfigs[cashType as CASH_DEPOSIT_CASH_TYPE];
  if (!config) return cashType;
  return config.label;
};

const CashDepositPrintPreview = ({ data }: ICashDepositPrintPreviewProps) => {
  const shopInfo = shopSelectors.data.shopInfo();
  const totalAmount = data.cashDeposits.reduce((sum, item) => sum + (item.total || 0), 0);

  return (
    <PrintContainer>
      <HeaderSection>
        <ShopName>{shopInfo?.shopName}</ShopName>
        <ShopAddress>{shopInfo?.address} {formatCityStateZip(shopInfo?.city, shopInfo?.state, shopInfo?.zip)}</ShopAddress>
        <ShopPhone>{shopInfo?.phone}</ShopPhone>
        <DateText>{data.createdDate}</DateText>
        <Divider />
      </HeaderSection>

      <StaffSection>
        <StaffHeader>
          <StaffName>{data.staffName?.toUpperCase()}</StaffName>
        </StaffHeader>
      </StaffSection>

      <TableSection>
        <TableHeader>
          <HeaderCell>CURRENCY</HeaderCell>
          <HeaderCell>QUANTITY</HeaderCell>
          <HeaderCell>AMOUNT</HeaderCell>
        </TableHeader>
        
        {data.cashDeposits.map((item) => {
          if (item.quantity === 0 && item.total === 0) return null;
          return (
            <TableRow key={item.cashType}>
              <TableCell>{getCashTypeLabel(item.cashType)}</TableCell>
              <TableCell>{item.quantity}</TableCell>
              <TableCell>{formatCurrency(item.total)}</TableCell>
            </TableRow>
          );
        })}
      </TableSection>

      <TotalSection>
        <TotalHeader>
          <TotalLabel>TOTAL</TotalLabel>
          <TotalAmount>{formatCurrency(totalAmount)}</TotalAmount>
        </TotalHeader>
      </TotalSection>
    </PrintContainer>
  );
};

export default CashDepositPrintPreview;

const PrintContainer = styled.div`
  background: white;
  font-family: 'Open Sans', Arial, sans-serif;
  width: 100%;
  height: fit-content;
`;

const HeaderSection = styled.div`
  text-align: center;
  padding: 4px;
`;

const ShopName = styled.div`
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 2px;
  color: black;
`;

const ShopAddress = styled.div`
  font-size: 12px;
  margin-bottom: 2px;
  color: black;
`;

const ShopPhone = styled.div`
  font-size: 12px;
  margin-bottom: 4px;
  color: black;
`;

const DateText = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-top: 4px;
  color: black;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid black;
  margin: 4px 8px;
`;

const StaffSection = styled.div`
  margin: 0 8px;
  margin-bottom: 8px;
`;

const StaffHeader = styled.div`
  background-color: black;
  color: white;
  padding: 8px;
  font-weight: bold;
  margin: 8px 0 0 0;
  display: block;
  width: 100%;
  box-sizing: border-box;
`;

const StaffName = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  text-align: center;
`;

const TableSection = styled.div`
  margin: 0 8px;
  margin-bottom: 10px;
`;

const TableHeader = styled.div`
  display: flex;
  padding: 6px 8px;
  border-bottom: 1px dashed black;
  font-size: 10px;
  font-weight: 600;
`;

const HeaderCell = styled.div`
  flex: 1;
  font-family: 'Open Sans';
  font-size: 12px;
  color: black;
  text-align: left;
`;

const TableRow = styled.div`
  display: flex;
  padding: 4px 8px;
  font-size: 12px;
`;

const TableCell = styled.div`
  flex: 1;
  font-family: 'Open Sans';
  font-size: 12px;
  color: black;
  text-align: left;
`;

const TotalSection = styled.div`
  margin: 0 8px;
`;

const TotalHeader = styled.div`
  background-color: black;
  color: white;
  padding: 8px 8px;
  font-weight: bold;
  margin: 8px 0 60px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
`;

const TotalLabel = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  padding: 0 8px;
`;

const TotalAmount = styled.div`
  font-size: 14px;
  text-align: right;
  padding: 0 8px;
`;